/**
 * ER-10191: Reset in-memory filter / view-state singletons on logout & Access switch.
 * Does not clear persisted per-Access filters on disk (filtersByAccess).
 *
 * Uses lazy requires so call sites like the axios 401 interceptor do not create
 * circular imports at module load time (axios → this util → view state → api → axios).
 */
function safeCall(fn) {
  try {
    fn?.();
  } catch (e) {
    /* ignore — modules may not be initialized yet */
  }
}

export function resetInMemoryFilterState() {
  safeCall(() => {
    const { useFiltersStore } = require("@/store/pinia/filters");
    useFiltersStore().resetFilters();
  });

  safeCall(() => {
    const store = require("@/store").default;
    store?.dispatch?.("filterStore/clearInMemoryFilters");
  });

  safeCall(() => {
    require("@/composables/positions/usePositionFilters").resetPositionFilters();
  });
  safeCall(() => {
    require("@/composables/employees/useEmployeeFilters").resetEmployeeFilters();
  });
  safeCall(() => {
    require("@/views/Positions/usePositionViewState").resetPositionViewState();
  });
  safeCall(() => {
    require("@/views/Employees/composables/useEmployeeViewState").resetEmployeeViewState();
  });
  safeCall(() => {
    require("@/views/ReadinessEmployees/composables/useReadinessEmployeeViewState").resetReadinessEmployeeViewState();
  });
  safeCall(() => {
    require("@/views/ResponsibilityDirectory/composables/useViewState").resetResponsibilityDirectoryViewState();
  });
  safeCall(() => {
    require("@/views/ReadinessCheckListView/composables/useReadinessChecklistViewState").resetReadinessChecklistViewState();
  });
  safeCall(() => {
    require("@/store/pinia/position").usePositionStore().resetPositions();
  });
  safeCall(() => {
    require("@components/organizations/OrganizationPositionsTable/useViewState").resetOrganizationPositionsViewState();
  });
  safeCall(() => {
    const {
      useEmployeesDirectoryTableViewState,
    } = require("@components/EmployeesDirectoryTable/composables/useViewState");
    const view = useEmployeesDirectoryTableViewState();
    if (!view?.state) return;
    const { state } = view;
    state.filters.values = [];
    state.filters.searchText = "";
    state.filters.showFilterDialog = false;
    state.filters.clearSelection = "";
    state.loading.table = false;
    state.table.currentPage = 1;
    state.table.itemsPerPage = 10;
    state.table.sortBy = [];
    state.table.sortDesc = [];
    state.table.search = "";
    state.embedded = false;
    state.showEmployeeBadgesList = false;
    state.showEmployeeReadinessPath = false;
    state.selectedEmployee = null;
    state.showResponsibilityDirectoryCard = false;
    state.selectedResponsibilityId = null;
    state.selectedResponsibilityDirectoryRow = null;
  });
  safeCall(() => {
    require("@components/EmployeesOrganizationTable/composables/useEmployeesOrganizationTableViewState").resetEmployeesOrganizationTableViewState();
  });
  safeCall(() => {
    require("@components/OrganizationRolesTable/composables/useOrganizationRolesTableViewState").resetOrganizationRolesTableViewState();
  });
  safeCall(() => {
    require("@components/OperatingCurrenciesTable/composables/useOperatingCurrenciesTableViewState").resetOperatingCurrenciesTableViewState();
  });
  safeCall(() => {
    require("@components/OperatingCountryTable/composables/useOperatingCountryTableViewState").resetOperatingCountryTableViewState();
  });
  safeCall(() => {
    require("@components/ResponsibilityBadgeHoldersTable/composables/useBadgeHoldersViewState").resetBadgeHoldersViewState();
  });
  safeCall(() => {
    require("@components/ResponsibilityBadgeHoldersTable/composables/useBadgeHolders").resetBadgeHolders();
  });

  // ER-12337: cancel any in-flight Generate Microlearning request and drop the
  // per-checkpoint runs so a background generation cannot outlive the session.
  safeCall(() => {
    const {
      useIqVideoMicrolearningStore,
    } = require("@components/IQOverlay/store/iqVideoMicrolearningStore");
    useIqVideoMicrolearningStore().clearAllRuns();
  });

  // ER-12337: cancel/forget the background video-processing + context-validation workflow
  // (Cloudflare polls, signed-URL lookups, video_to_components stream) on logout.
  safeCall(() => {
    const {
      useIqVideoProcessingStore,
    } = require("@components/IQOverlay/store/iqVideoProcessingStore");
    useIqVideoProcessingStore().clearAll();
  });

  // ER-12337: cancel/forget background quiz + inspection suggestion streams on logout.
  safeCall(() => {
    const {
      useIqCheckpointSuggestStore,
    } = require("@components/IQOverlay/store/iqCheckpointSuggestStore");
    useIqCheckpointSuggestStore().clearAll();
  });
}
